'use client';

import type { UpdateListInput } from '@swishapp/api-client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLogger } from 'next-axiom';
import { toast } from 'sonner';
import { WISHLIST_QUERY_KEYS } from '../wishlist-constants';
import type { CreateItemInput } from '../wishlist-proxy-client';
import { WishlistProxyClient } from '../wishlist-proxy-client';
import type { WishlistError } from '../wishlist-types';
import { useWishlistCacheInvalidation } from './use-wishlist-cache-invalidation';
import { useWishlistSessionToken } from './use-wishlist-session-token';

function formatWishlistError(error: WishlistError): string {
  return typeof error === 'object' && error.message
    ? error.message
    : String(error);
}

function isAuthError(error: WishlistError): boolean {
  return error.code === 'AUTH_ERROR';
}

/**
 * Hook for wishlist mutations with proper cache invalidation
 */
export function useWishlistMutations() {
  const { data: authToken, refetch: refetchToken } = useWishlistSessionToken();
  const log = useLogger().with({ location: 'useWishlistMutations' });
  const cacheInvalidation = useWishlistCacheInvalidation();
  const queryClient = useQueryClient();

  const client = authToken ? new WishlistProxyClient(authToken) : null;

  // Create list mutation with auth error handling
  const createListMutation = useMutation({
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    mutationFn: async (name: string) => {
      if (!client) throw new Error('No auth token available');

      let currentClient = client;
      let retryCount = 0;
      const maxRetries = 1;

      while (retryCount <= maxRetries) {
        const result = await currentClient.createList({ name });

        if (result.error) {
          // Check if it's an auth error and we haven't retried yet
          if (isAuthError(result.error) && retryCount < maxRetries) {
            // Auth error detected, refreshing token

            // Clear the existing token from cache
            queryClient.setQueryData([WISHLIST_QUERY_KEYS.SESSION_TOKEN], null);

            // Force refetch a new token
            const tokenResult = await refetchToken({ forceRefresh: true });

            if (tokenResult.data) {
              // Create new client with fresh token
              currentClient = new WishlistProxyClient(tokenResult.data);
              retryCount++;

              // Retrying with fresh token
              // Continue to retry with the new client
              continue;
            }
          }

          // If not an auth error or retry failed, log and throw
          log.error('Create list failed', { error: result.error, name });
          throw new Error(formatWishlistError(result.error));
        }

        // Success
        return result.data;
      }

      // This should never be reached, but TypeScript needs it
      throw new Error('Max retries exceeded');
    },
    onSuccess: () => {
      cacheInvalidation.invalidateAfterListChange();
    },
    onError: (error) => {
      toast.error(`Failed to create board: ${error.message}`);
    },
  });

  // Update list mutation
  const updateListMutation = useMutation({
    mutationFn: async ({
      listId,
      input,
    }: { listId: string; input: UpdateListInput }) => {
      if (!client) throw new Error('No auth token available');

      const result = await client.updateList(listId, input);

      if (result.error) {
        log.error('Update list failed', { error: result.error, listId, input });
        throw new Error(formatWishlistError(result.error));
      }

      return result.data;
    },
    onSuccess: () => {
      cacheInvalidation.invalidateAfterListChange();
    },
    onError: (error) => {
      toast.error(`Failed to update board: ${error.message}`);
    },
  });

  // Delete list mutation
  const deleteListMutation = useMutation({
    mutationFn: async (listId: string) => {
      if (!client) throw new Error('No auth token available');

      const result = await client.deleteList(listId);

      if (result.error) {
        log.error('Delete list failed', { error: result.error, listId });
        throw new Error(formatWishlistError(result.error));
      }

      return result.data;
    },
    onSuccess: (_, listId) => {
      cacheInvalidation.invalidateAfterListChange();
      cacheInvalidation.removeListFromCache(listId);
    },
    onError: (error) => {
      toast.error(`Failed to delete board: ${error.message}`);
    },
  });

  // Add item mutation with auth error handling and retry
  const addItemMutation = useMutation({
    // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    mutationFn: async (item: CreateItemInput) => {
      if (!client) throw new Error('No auth token available');

      let currentClient = client;
      let retryCount = 0;
      const maxRetries = 1;

      while (retryCount <= maxRetries) {
        const result = await currentClient.addItem(item);

        if (result.error) {
          // Check if it's an auth error and we haven't retried yet
          if (isAuthError(result.error) && retryCount < maxRetries) {
            // Auth error detected, refreshing token

            // Clear the existing token from cache
            queryClient.setQueryData([WISHLIST_QUERY_KEYS.SESSION_TOKEN], null);

            // Force refetch a new token
            const tokenResult = await refetchToken({ forceRefresh: true });

            if (tokenResult.data) {
              // Create new client with fresh token
              currentClient = new WishlistProxyClient(tokenResult.data);
              retryCount++;

              // Retrying with fresh token
              // Continue to retry with the new client
              continue;
            }
          }

          // If not an auth error or retry failed, log and throw
          log.error('Add item failed', { error: result.error, item });
          throw new Error(formatWishlistError(result.error));
        }

        // Success
        return result.data;
      }

      // This should never be reached, but TypeScript needs it
      throw new Error('Max retries exceeded');
    },
    onMutate: async (newItem: CreateItemInput) => {
      // Use centralized optimistic update function
      if (newItem.listId) {
        await cacheInvalidation.cancelBoardQueries(newItem.listId);
        const rollbackData =
          cacheInvalidation.optimisticallyAddItemToBoard(newItem);
        return rollbackData;
      }
      // Handle "All Items" additions without listId
      await cacheInvalidation.cancelAllItemsQueries();
      const rollbackData =
        cacheInvalidation.optimisticallyAddItemToAllItems(newItem);
      return rollbackData;
    },
    onError: (error, _newItem, context) => {
      // Revert the optimistic update using centralized function
      if (context) {
        cacheInvalidation.revertOptimisticUpdate(context);
      }

      toast.error(`Failed to add item to wishlist: ${error.message}`);
    },
    onSuccess: (_, item) => {
      cacheInvalidation.invalidateAfterItemChange(item.listId);
    },
  });

  // Remove item mutation (COMPLETE DELETION from wishlist)
  const removeItemMutation = useMutation({
    mutationFn: async (itemId: string) => {
      if (!client) throw new Error('No auth token available');

      const result = await client.removeItem(itemId);

      if (result.error) {
        log.error('Remove item failed', { error: result.error, itemId });
        throw new Error(formatWishlistError(result.error));
      }

      return result.data;
    },
    onMutate: async (itemId: string) => {
      // Cancel any outgoing refetches for all relevant queries
      await queryClient.cancelQueries({
        queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
      });
      await queryClient.cancelQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST],
      });

      // Snapshot the previous values
      const previousItems = queryClient.getQueryData([
        WISHLIST_QUERY_KEYS.ALL,
        'items',
      ]);

      // Optimistically update the items cache
      queryClient.setQueryData(
        [WISHLIST_QUERY_KEYS.ALL, 'items'],
        (old: unknown[] = []) =>
          (old as { id: string }[]).filter((item) => item.id !== itemId)
      );

      // Also optimistically update all list caches
      queryClient.setQueriesData(
        { queryKey: [WISHLIST_QUERY_KEYS.LIST] },
        (oldData: unknown) => {
          if (oldData && typeof oldData === 'object' && 'items' in oldData) {
            const listData = oldData as { items?: { id: string }[] };
            return {
              ...listData,
              items: (listData.items || []).filter(
                (item) => item.id !== itemId
              ),
            };
          }
          return oldData;
        }
      );

      return { previousItems };
    },
    onError: (error, _itemId, context) => {
      // Revert the optimistic update on error
      if (context?.previousItems) {
        queryClient.setQueryData(
          [WISHLIST_QUERY_KEYS.ALL, 'items'],
          context.previousItems
        );
      }

      // Also invalidate all list caches to refresh from server
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST],
      });

      toast.error(`Failed to remove item from wishlist: ${error.message}`);
    },
    onSuccess: () => {
      // We don't know which list the item was in, so don't pass listId
      cacheInvalidation.invalidateAfterItemChange();
    },
  });

  // Link item to board mutation with auth error handling
  const linkItemToBoardMutation = useMutation({
    mutationFn: async ({
      itemId,
      boardId,
      // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
    }: { itemId: string; boardId: string }) => {
      if (!client) throw new Error('No auth token available');

      let currentClient = client;
      let retryCount = 0;
      const maxRetries = 1;

      while (retryCount <= maxRetries) {
        const result = await currentClient.linkItemToBoard(itemId, boardId);

        if (result.error) {
          // Check if it's an auth error and we haven't retried yet
          if (isAuthError(result.error) && retryCount < maxRetries) {
            // Auth error detected, refreshing token

            // Clear the existing token from cache
            queryClient.setQueryData([WISHLIST_QUERY_KEYS.SESSION_TOKEN], null);

            // Force refetch a new token
            const tokenResult = await refetchToken({ forceRefresh: true });

            if (tokenResult.data) {
              // Create new client with fresh token
              currentClient = new WishlistProxyClient(tokenResult.data);
              retryCount++;

              // Retrying with fresh token
              // Continue to retry with the new client
              continue;
            }
          }

          // If not an auth error or retry failed, log and throw
          log.error('Link item to board failed', {
            error: result.error,
            itemId,
            boardId,
          });
          throw new Error(formatWishlistError(result.error));
        }

        // Success
        return result.data;
      }

      // This should never be reached, but TypeScript needs it
      throw new Error('Max retries exceeded');
    },
    onSuccess: (_, { boardId }) => {
      // Invalidate the specific board cache to show the new item
      cacheInvalidation.invalidateAfterItemChange(boardId);
    },
    onError: (error) => {
      toast.error(`Failed to add item to board: ${error.message}`);
    },
  });

  // Remove item from board mutation (board-specific removal)
  const removeItemFromBoardMutation = useMutation({
    mutationFn: async ({
      itemId,
      boardId,
    }: { itemId: string; boardId: string }) => {
      if (!client) throw new Error('No auth token available');

      const result = await client.removeItemFromBoard(itemId, boardId);

      if (result.error) {
        log.error('Remove item from board failed', {
          error: result.error,
          itemId,
          boardId,
        });
        throw new Error(formatWishlistError(result.error));
      }

      return result.data;
    },
    onMutate: async ({ itemId, boardId }) => {
      // Cancel any outgoing refetches for the specific board only
      await cacheInvalidation.cancelBoardQueries(boardId);

      // Snapshot the previous board data
      const boardQueryKey = [WISHLIST_QUERY_KEYS.LIST, boardId, authToken];
      const previousBoardData = queryClient.getQueryData(boardQueryKey);

      // Optimistically remove item from the specific board ONLY
      // CRITICAL: Do NOT touch the "All Items" cache - the item still exists globally
      queryClient.setQueryData(boardQueryKey, (oldData: unknown) => {
        if (oldData && typeof oldData === 'object' && 'items' in oldData) {
          const boardData = oldData as { items?: { id: string }[] };
          return {
            ...boardData,
            items: (boardData.items || []).filter((item) => item.id !== itemId),
          };
        }
        return oldData;
      });

      return { previousBoardData, boardQueryKey };
    },
    onError: (error, { boardId }, context) => {
      // Revert the optimistic update on error
      if (context?.previousBoardData && context?.boardQueryKey) {
        queryClient.setQueryData(
          context.boardQueryKey,
          context.previousBoardData
        );
      }

      // Only invalidate the specific board on error
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST, boardId],
      });

      toast.error(`Failed to remove item from board: ${error.message}`);
    },
    onSuccess: (data, { boardId }) => {
      // Only invalidate the specific board
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LIST, boardId],
      });

      // Invalidate the lists query to update board item counts
      queryClient.invalidateQueries({
        queryKey: [WISHLIST_QUERY_KEYS.LISTS],
      });

      // If the item was recreated (removed from last board), we need to invalidate all items
      // to pick up the new item ID
      if (data && typeof data === 'object' && 'newItemId' in data) {
        // Item was recreated with new ID, invalidating all items
        queryClient.invalidateQueries({
          queryKey: [WISHLIST_QUERY_KEYS.ALL, 'items'],
        });
      }
    },
  });

  return {
    createList: createListMutation.mutate,
    updateList: updateListMutation.mutate,
    deleteList: deleteListMutation.mutate,
    addItem: addItemMutation.mutate,
    removeItem: removeItemMutation.mutate,
    linkItemToBoard: linkItemToBoardMutation.mutate,
    removeItemFromBoard: removeItemFromBoardMutation.mutate,

    // Loading states
    isCreatingList: createListMutation.isPending,
    isUpdatingList: updateListMutation.isPending,
    isDeletingList: deleteListMutation.isPending,
    isAddingItem: addItemMutation.isPending,
    isRemovingItem: removeItemMutation.isPending,
    isLinkingItemToBoard: linkItemToBoardMutation.isPending,
    isRemovingItemFromBoard: removeItemFromBoardMutation.isPending,

    // Mutations for more control
    createListMutation,
    updateListMutation,
    deleteListMutation,
    addItemMutation,
    removeItemMutation,
    linkItemToBoardMutation,
    removeItemFromBoardMutation,
  };
}
